import { Dispatch, ReactNode, SetStateAction } from "react";
import { Appearance } from "@stripe/stripe-js";

import joy from "../../images/landing/joy.jpeg";
import precious from "../../images/landing/precious.jpeg";
import henry from "../../images/landing/henry.jpeg";

export const annualDiscountPercent = 20;
export const customerTrialDays = 7;

export const paymentPlans: PaymentPlans = [
  {
    name: "Free",
    value: "Free",
    tagline: "",
    rate: "$0",
    features: [
      "8020 Learning Paths.",
      "Limited Instant Matching & Alerts – Spot and pitch projects before others (Limited)",
      "OpenSource Project Finder.",
      "Dev AI Agent.",
      "Proposal Crafting (Limited)",
      "Community Support.",
    ],
    cta: "Start with Free",
  },
  {
    name: "Pro",
    value: "Pro",
    tagline: "⭐ Most Popular — Chosen by 80% of freelancers",
    features: [
      "All Free Plan features",
      "Unlimited Matching & Alerts – Be first to pitch on Upwork & LinkedIn",
      "Niche Finder – Target the most profitable opportunities",
      "Unlimited Proposal Crafting – Send polished proposals anytime",
      "2x Faster AI Help – Draft proposals in seconds",
      "Priority Email Support",
    ],
    cta: "Subscribe",
    currency: "$",
    currencyId: "usd",
    why: "Land just one project and your Pro plan pays for itself many times over.",
    productId: process.env.REACT_APP_TUBLIAN_PRO_PLAN_PRODUCT_ID,
  },
];

export const testimonialsData = [
  {
    name: "Joy Oluwafemi",
    handle: "Front-end Engineer",
    testimonial:
      "Agencer has completely changed how I job hunt. I don’t have to spend hours refreshing job boards anymore. It quietly works in the background, finds opportunities that match my skills, and sends me instant notifications. I’ve already applied to more relevant jobs in a week than I used to in a month!",
    image: joy,
  },
  {
    name: "Precious Ngube",
    handle: "Community Manager",
    testimonial:
      "This extension feels like having a personal assistant for my career. While I focus on my daily work, Agencer is out there scanning LinkedIn and Upwork for the right gigs and alerting me immediately. I’ve never had such a stress-free and efficient job search.",
    image: precious,
  },
  {
    name: "Henry Udaeze",
    handle: "Product Designer",
    testimonial:
      "With Agencer, I don’t waste time scrolling through endless listings anymore. It automatically finds the jobs that fit me best and notifies me right away. It’s like the job search happens while I’m busy living my life.",
    image: henry,
  },
];

export const calculateMonthlyEquivalent = (yearlyAmount: number) => {
  const monthlyEquivalent = yearlyAmount / 12;
  return monthlyEquivalent.toFixed(2);
};

export type PaymentPlan = "Free" | "Freelancer" | "Pro";

export type PaymentPlanName = "Free" | "Freelancer" | "Pro";

export type BillingFreq = "monthly" | "annually";

export type IndividualPaymentPlan = {
  name: PaymentPlanName;
  value: PaymentPlan;
  tagline: string;
  rate?: number | string;
  recommended?: boolean;
  features: string[];
  cta: string;
  currency?: string;
  currencyId?: string;
  productId?: string;
  why?: string;
};

export type PaymentPlans = IndividualPaymentPlan[];

export interface Price {
  id: string;
  unit_amount: number;
  currency: string;
  currency_options: {
    [key: string]: {
      custom_unit_amount: null;
      tax_behavior: string;
      unit_amount: number;
      unit_amount_decimal: string;
    };
  };
  credits: string;
  recurring: {
    aggregate_usage: null;
    interval: string;
    interval_count: number;
    meter: null;
    trial_period_days: null;
    usage_type: string;
  };
}
export type Product = {
  id: string;
  name: string;
  description: string | null;
  prices: Price[];
};

export interface StepPaymentPlansProps {
  mode?: "onboarding" | "editProfile";
}

export type PaymentPlanDetails = {
  billingFreq: BillingFreq;
  selectedPaymentPlan: IndividualPaymentPlan;
};

export type BillingOption = {
  name: string;
  label: string | ReactNode;
};

export type UpdatePlanDetails = (
  fieldName: keyof PaymentPlanDetails,
  value: string | number | IndividualPaymentPlan
) => void;

export type PopupDataType = {
  isActive: boolean;
  msg: string;
  status: "error" | "success" | null;
};

export type Invoice = {
  id: string;
  amountPaid: string;
  receiptUrl: string;
  status: string;
  date: string;
};

export type Subscription = {
  subscriptionId: string;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  subscriptionPlan: string;
  billingInterval: string;
  invoices: Invoice[];
  paymentMethod?: string;
};

export type AccountSubscriptionPlan = {
  subscriptionPlan: string;
  billingInterval: string;
  amountPaid: number;
  subscriptionId: string;
  customerId: string;
  currency: string;
  isPendingCancel?: boolean;
  cancelAt?: number;
  paymentMethod?: {
    brand: string;
    last4: string;
  };
};

export const stripeAppearance: Appearance = {
  theme: "night",
  labels: "floating",
  rules: {
    ".Tab": {
      border: "1px solid #292929",
      borderRadius: "10px",
      padding: "18px 20px",
      color: "#5F6368",
      backgroundColor: "#1E1E1E",
      boxShadow: "none",
    },
    ".Tab:hover": {
      color: "#FEFEFE",
    },
    ".Tab--selected": {
      border: "1px solid #FDD649",
      backgroundColor: "transparent",
      color: "#FEFEFE",
    },
    ".Tab--selected:hover": {
      border: "1px solid #FDD649",
      backgroundColor: "transparent",
      color: "#FEFEFE",
    },
    ".Input": {
      backgroundColor: "transparent",
      border: "none",
      borderRadius: "0px",
      borderBottom: "1px solid #888",
      boxShadow: "none",
      color: "#888",
    },
    ".Input:focus": {
      backgroundColor: "transparent",
      border: "none",
      borderRadius: "0px",
      borderBottom: "1px solid #FEFEFE",
      boxShadow: "none",
    },
    ".Label--resting": {
      color: "#888",
      WebkitTextFillColor: "#888",
    },
    ".Label--floating": {
      color: "#FEFEFE",
      WebkitTextFillColor: "#FEFEFE",
    },
    ".Icon": {
      // fill: "#FEFEFE",
    },
    ".Label--empty": {
      color: "#888",
      WebkitTextFillColor: "#888",
    },
    ".Input--invalid": {
      backgroundColor: "transparent",
      border: "none",
      borderRadius: "0px",
      borderBottom: "1px solid #F26663",
      boxShadow: "none",
      color: "#F26663",
    },
    ".AccordionItem": {
      color: "#FEFEFE",
      backgroundColor: "#1E1E1E",
      boxShadow: "none",
      borderRadius: "20px",
    },
  },
  variables: {
    // fontFamily: "Space Grotesk",
    colorPrimary: "#FEFEFE",
    colorBackground: "#1E1E1E",
    colorText: "#888",
    colorDanger: "#F26663",
    colorTextPlaceholder: "#888",

    colorIcon: "#FEFEFE",
    logoColor: "dark",
    iconColor: "#888",
    tabLogoColor: "dark",
    blockLogoColor: "dark",
    tabLogoSelectedColor: "light",
    tabIconColor: "#888",
    tabIconSelectedColor: "#FEFEFE",
    iconChevronDownColor: "#FEFEFE",

    iconCheckmarkColor: "#FEFEFE",

    iconCardErrorColor: "#F26663",
    iconCardCvcErrorColor: "#F26663",
  },
};

export const faqs = [
  {
    id: "faq2",
    question: "Can I upgrade or downgrade anytime?",
    answer:
      "Yes! You can switch plans anytime from your account dashboard. No penalties.",
  },
  {
    id: "faq3",
    question: "How does the Agencer find freelance projects for me?",
    answer:
      "The AI analyzes your skills, experience, and preferences to scout the most relevant freelance gigs across multiple platforms — helping you find projects you’re more likely to win.",
  },
  {
    id: "faq7",
    question: "If I’m on the Free Plan, will I still get support?",
    answer:
      "Absolutely. Free users get access to our community forum. Paid users get priority email or chat support depending on their plan.",
  },
  {
    id: "faq8",
    question: "Are there discounts for teams?",
    answer:
      "Yes! We offer special rates for teams and startups. Contact hello@tublian.com for more info.",
  },
];

export interface IPricingContext {
  isLoadingProfile: boolean;
  paymentPlanDetails: PaymentPlanDetails;
  fetchProfile: () => Promise<void>;
  setPaymentPlanDetails: Dispatch<SetStateAction<PaymentPlanDetails>>;
  updatePlanDetails: UpdatePlanDetails;
  setAccountSubscriptionPlan: React.Dispatch<
    React.SetStateAction<AccountSubscriptionPlan | null>
  >;
  accountSubscriptionPlan: AccountSubscriptionPlan | null;
  submitted: boolean;
  setSubmitted: Dispatch<SetStateAction<boolean>>;
  fetchProduct: (
    productName: string,
    productId: string | undefined
  ) => Promise<void>;
  products: Record<string, Product>;
  getPrice: (productName: string | null, interval: string) => Price | null;
  getDisplayAmount: (unitAmount: number) => string;
  errorMessage: string;
  setErrorMessage: Dispatch<SetStateAction<string>>;
  loadingSubscription: boolean;
  setLoadingSubscription: Dispatch<SetStateAction<boolean>>;
  loadingPackages: string;
  paymentStatusPopup: PopupDataType;
  setPaymentStatusPopup: Dispatch<SetStateAction<PopupDataType>>;
  nextBillingDate: Date | null;
  setNextBillingDate: Dispatch<SetStateAction<Date | null>>;
  invoices: (Invoice & { subscriptionPlan: string; billingInterval: string })[];
  setInvoices: Dispatch<
    SetStateAction<
      (Invoice & { subscriptionPlan: string; billingInterval: string })[]
    >
  >;
  paymentHistory: (useRetry?: boolean) => Promise<void>;
  paymentStatus: "success" | "error" | "loading" | null;
  setPaymentStatus: React.Dispatch<
    React.SetStateAction<"success" | "error" | "loading" | null>
  >;

  // ✅ Replaces all individual modal fields
  modalState: {
    showChangePlan: boolean;
    showCancelPlanToast: boolean;
    showChangePlanToast: boolean;
    showPlanFeedback: boolean;
    showPaymentPage: boolean;
    showAllInvoices: boolean;
    cancelSubPlan: boolean;
  };
  toggleModal: (
    key: keyof IPricingContext["modalState"],
    value: boolean
  ) => void;
  isLoadingInvoices: boolean;
  setIsLoadingInvoices: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface UserProfile {
  isAccountPro: boolean;
  bio: string;
  cohort: string;
  createdAt: number;
  learningCredits: string;
  isAdmin: boolean;
  name: string;
  reference: string;
  roles: string;
  emailAddress: string;
  userId: string;
  members: string[];
  interestAreas: string[];
  showWelcomeMessage: boolean;
  tags: string[];
  languages: string[];
  jwt: string;
  accountSubscriptionPlan: string;
}
